/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject, SFTPConnection} from '@ion/core/src/types/entities';

/**
 * It defines fields related to SFTP configuration, such as
 * server name, host, port, username, password, RSA private key,
 * and allowed accounts.
 */
export enum SFTPConfigurationFields {
  Name = 'serverName',
  Host = 'host',
  Port = 'port',
  Username = 'username',
  Password = 'password',
  RSAPrivateKey = 'privateKey',
  AllowedAccounts = 'allowedAccounts'
};

/**
 * Deserializes an SFTPConnection object into a structured configuration object.
 *
 * ```typescript
 *
 *  import {deserialize} from './SFTPConfigurationEditorUtils';
 *
 *  const deserialized = deserialize(config);
 *
 * ```
 * @param config - The SFTPConnection object to deserialize.
 * @returns An object with structured configuration fields.
 */
export const deserialize = (config: SFTPConnection, omitSubAccounts = true) => {
  return {
    [SFTPConfigurationFields.Name] : {
      value    : config[SFTPConfigurationFields.Name] || '',
      required : true
    },
    [SFTPConfigurationFields.Host] : {
      value    : config[SFTPConfigurationFields.Host] || '',
      required : true
    },
    [SFTPConfigurationFields.Port] : {
      value    : config[SFTPConfigurationFields.Port] || '',
      required : true
    },
    [SFTPConfigurationFields.Username] : {
      value    : config[SFTPConfigurationFields.Username] || '',
      required : true
    },
    [SFTPConfigurationFields.Password] : {
      value : config[SFTPConfigurationFields.Password] || ''
    },
    [SFTPConfigurationFields.RSAPrivateKey] : {
      value : config[SFTPConfigurationFields.RSAPrivateKey]?.replaceAll?.('\n', '\\n') || ''
    },
    ...(!omitSubAccounts ? {
      [SFTPConfigurationFields.AllowedAccounts] : {
        value : config[SFTPConfigurationFields.AllowedAccounts] || []
      }
    } : {})
  };
};

/**
 * Serializes an SFTPConnection object into a format suitable for transmission.
 *
 * ```typescript
 *
 *  import {serializer} from './SFTPConfigurationEditorUtils';
 *
 *  const serialized = serializer(config);
 *
 * ```
 * @param config - The SFTPConnection object to serialize.
 * @returns A serialized object with transformed values.
 */
export const serializer = (config: GenericObject) => {
  const serialized = {};
  for (const [key, state] of Object.entries(config)) {
    serialized[key] = key === SFTPConfigurationFields.RSAPrivateKey
      ? state.value?.replaceAll?.('\\n', '\n')
      : state.value;
  }
  return serialized;
};
